import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import {
  FaEdit,
  FaArrowRight,
  FaTimes,
  FaWrench,
  FaCarSide,
  FaTrash,
  FaChartBar,
  FaUserShield,
  FaUndo,
  FaRedo,
  FaFileAlt,
  FaAngleLeft,
  FaAngleRight,
  FaThumbsUp,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import {
  MDBCheckbox,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
} from "mdb-react-ui-kit";

import { supabase } from "../../supabaseClient";
import { useReportConditions } from "../../hooks/queries";
import { useSearchSchedules } from "../../hooks/mutation";

import HeaderContainer from "./HeaderContainer";
import CustomSelect from "../common/select";
import TextInput from "../common/TextInput";
import DateInput from "../common/DateInput";

import "mdb-react-ui-kit/dist/css/mdb.min.css";
import "leaflet/dist/leaflet.css";

const CrewContainer = () => {
  const navigate = useNavigate(); // Use useNavigate instead of useHistory

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignRideModalOpen, setIsAssignRideModalOpen] = useState(false);
  const [safetyCheck, setSafetyCheck] = useState({
    clean: true,
    fluids: true,
    oil: false,
    lights: false,
    wipers: false,
    tires: false,
  });
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // Recently-created Add-Trip IDs stashed by TripAdd in sessionStorage.
  // We consume them on mount and float those rows to the top of the
  // schedule list with a glow so the manager sees exactly what just
  // saved. Cleared on any new search.
  const [recentlyAddedIds, setRecentlyAddedIds] = useState(() => {
    try {
      const raw = sessionStorage.getItem("recentlyCreatedTripIds");
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      const ids = Array.isArray(parsed) ? parsed : parsed.ids || [];
      sessionStorage.removeItem("recentlyCreatedTripIds");
      return new Set(ids.map((x) => Number(x)).filter(Number.isFinite));
    } catch {
      return new Set();
    }
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      flightNo: "",
      scheduleDate: "",
      passengers: "",
      companyID: undefined,
      airlineID: undefined,
      driverID: undefined,
      vehicleID: undefined,
      locationID: undefined,
    },
  });

  const { data: conditions, isSuccess: successConditions } =
    useReportConditions();
  
  // Fetch drivers and vehicles when assign modal opens
  useEffect(() => {
    const fetchDriversAndVehicles = async () => {
      if (isAssignRideModalOpen) {
        const currentManagerId = parseInt(sessionStorage.getItem("UserId") ?? "0");
        
        try {
          // Fetch drivers
          const { data: driversData } = await supabase
            .from("driver_profiles")
            .select(`
              auth_id,
              first_name,
              last_name,
              email,
              Drivers!inner(DriverID, FirstName, LastName, UserName, DriverCode)
            `)
            .eq("manager_id", currentManagerId);
          
          if (driversData) {
            const formattedDrivers = driversData.map(driver => ({
              DriverID: driver.Drivers.DriverID,
              FirstName: driver.Drivers.FirstName || driver.first_name,
              LastName: driver.Drivers.LastName || driver.last_name,
              UserName: driver.Drivers.UserName || driver.email,
              DriverCode: driver.Drivers.DriverCode
            }));
            setDrivers(formattedDrivers);
          }
          
          // Fetch vehicles
          const { data: vehiclesData } = await supabase
            .from("Vehicles")
            .select("VehicleID, Code, Make, Model");
          
          if (vehiclesData) {
            setVehicles(vehiclesData);
          }
        } catch (error) {
          console.error("Error fetching drivers and vehicles:", error);
        }
      }
    };
    
    fetchDriversAndVehicles();
  }, [isAssignRideModalOpen]);
  const [companyList, clientList, driverList, vehicleList, locationList] =
    useMemo(() => {
      if (successConditions) {
        const {
          airlineList,
          vehicleList,
          companyList,
          locationList,
          driverList,
        } = conditions;

        return [
          companyList.map((_item) => ({
            label: _item.Description,
            value: _item.CompanyID,
          })),
          airlineList.map((_item) => ({
            label: _item.Description,
            value: _item.AirlineID,
          })),
          driverList.map((_item) => ({
            label: `${_item.FirstName} ${_item.LastName}`,
            value: _item.DriverID,
          })),
          vehicleList.map((_item) => ({
            label: _item.Model,
            value: _item.VehicleID,
          })),
          locationList.map((_item) => ({
            label: _item.Description,
            value: _item.LocationID,
          })),
        ];
      } else return [[], [], [], [], []];
    }, [conditions, successConditions]);
  const onSuccessSearch = useCallback((_data) => {
    setTotalCount(_data.count);
    setSchedules(_data.list);
  }, []);
  const { mutate: searchSchedules, isPending: isSearching } =
    useSearchSchedules(onSuccessSearch);

  // When the manager returns from Add Trip with fresh IDs, kick off an
  // initial broad search so the new trips are visible on the list.
  // Fires once on mount.
  useEffect(() => {
    if (recentlyAddedIds.size === 0) return;
    const today = new Date();
    const in60 = new Date(today.getTime() + 60 * 24 * 3600 * 1000);
    const fmt = (d) => d.toISOString().slice(0, 10);
    const userLocationID = parseInt(
      sessionStorage.getItem("LocationID") ?? "0"
    );
    searchSchedules({
      startDate: fmt(today),
      endDate: fmt(in60),
      locationID: userLocationID,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(
    (_data) => {
      // Fresh search — drop the "just-added" highlight so old glows
      // don't linger on unrelated results.
      setRecentlyAddedIds(new Set());
      const userLocationID = parseInt(
        sessionStorage.getItem("LocationID") ?? "0"
      );

      searchSchedules({
        startDate: _data.scheduleDate,
        endDate: _data.scheduleDate,
        driverID: _data.driverID,
        vehicleID: _data.vehicleID,
        airlineID: _data.airlineID,
        companyID: _data.companyID,
        passengers: _data.passengers,
        flightNo: _data.flightNo,
        locationID: userLocationID, //_data.locationID,
      });
    },
    [searchSchedules]
  );
  const onClear = useCallback(() => {
    reset();
  }, [reset]);

  const onNextPage = useCallback(() => {
    setPage((old) => {
      const newPage = old + 1;
      const _data = getValues();
      searchSchedules({
        ..._data,
        page: newPage,
      });
      return newPage;
    });
  }, [searchSchedules, getValues]);
  const onPrevPage = useCallback(() => {
    setPage((old) => {
      const newPage = old - 1;
      const _data = getValues();
      searchSchedules({
        ..._data,
        page: newPage,
      });
      return newPage;
    });
  }, [searchSchedules, getValues]);

  useEffect(() => {
    const fetchVehicles = async () => {
      const { data, error } = await supabase
        .from("Vehicles")
        .select("VehicleID, Code, Make, Model");

      if (error) {
        console.error("Error fetching vehicles:", error.message);
      } else {
        setVehicles(data);
        setSelectedVehicleId(data[0].VehicleID);
      }
    };

    fetchVehicles();
  }, []);

  useEffect(() => {
    const fetchDrivers = async () => {
      // Get current manager's UserID for filtering
      const currentManagerId = parseInt(sessionStorage.getItem("UserId"));
      const userRole = sessionStorage.getItem("UserRole");
      
      let query = supabase
        .from("driver_auth")
        .select(
          `
          id,
          email,
          is_active,
          driver_profiles!inner (
            id,
            first_name,
            last_name,
            phone,
            license_number,
            manager_id
          )
        `
        )
        .eq("is_active", true);

      // If not admin, filter by manager
      if (userRole !== "1" && currentManagerId) {
        query = query.eq("driver_profiles.manager_id", currentManagerId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching drivers:", error.message);
      } else {
        // Transform data to match expected format
        const transformedDrivers =
          data?.map((driver) => ({
            DriverID: driver.id,
            DriverCode: driver.driver_profiles?.[0]?.license_number || "",
            UserName:
              `${driver.driver_profiles?.[0]?.first_name || ""} ${driver.driver_profiles?.[0]?.last_name || ""
                }`.trim() || driver.email,
            Email: driver.email,
            FirstName: driver.driver_profiles?.[0]?.first_name || "",
            LastName: driver.driver_profiles?.[0]?.last_name || "",
            Phone: driver.driver_profiles?.[0]?.phone || "",
            IsActive: driver.is_active,
          })) || [];

        setDrivers(transformedDrivers);
        if (transformedDrivers.length > 0) {
          setSelectedDriverId(transformedDrivers[0].DriverID);
        }
      }
    };

    fetchDrivers();
  }, []);

  useEffect(() => {
    const userLocationID = parseInt(
      sessionStorage.getItem("LocationID") ?? "0"
    );

    searchSchedules({
      locationID: userLocationID,
    });
  }, [searchSchedules]);

  const toggleSafetyCheck = useCallback((key) => {
    setSafetyCheck((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const toggleModal = useCallback(() => {
    setIsModalOpen((old) => !old);
  }, []);

  const toggleCheckbox = useCallback((id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  }, []);

  const onManagerDetail = useCallback(() => {
    navigate("/manager-detail");
  }, [navigate]);
  const onSuperAdmin = useCallback(() => {
    navigate("/safe-super-admin");
  }, [navigate]);

  const onDriverDetail = useCallback(() => {
    navigate("/manager-drivers");
  }, [navigate]);
  const onManagerVehicles = useCallback(() => {
    navigate("/manager-vehicles");
  }, [navigate]);
  const handleReport = useCallback(() => {
    navigate("/reports");
  }, [navigate]);
  const handleFlightStatus = useCallback(() => {
    navigate("/flight-status");
  }, [navigate]);
  const handleScheduleChange = useCallback(() => {
    navigate("/schedule-change");
  }, [navigate]);
  const handleAddNote = useCallback(() => {
    navigate("/add-note");
  }, [navigate]);
  const handleAdminLogin = useCallback(() => {
    navigate("/login");
  }, [navigate]);
  const handleDriverInteraction = useCallback(() => {
    navigate("/driver-interaction");
  }, [navigate]);
  const handleAssignRide = useCallback(() => {
    // Check if a schedule is selected before opening the assignment modal
    if (selectedRows.length === 0) {
      toast.error(
        "Please select a schedule/trip first before assigning a driver."
      );
      return;
    }

    // For now, we'll use the first selected schedule
    // In the future, this could be enhanced to handle multiple selections
    const scheduleId = selectedRows[0];
    const schedule = schedules.find((s) => s.ScheduleID === scheduleId);

    if (!schedule) {
      toast.error("Selected schedule not found.");
      return;
    }

    setSelectedScheduleId(scheduleId);
    setSelectedSchedule(schedule);
    setIsAssignRideModalOpen(true);
    // Don't clear selectedRows here - keep the selection visible
  }, [navigate, selectedRows, schedules]);
  const handleImport = useCallback(() => {
    navigate("/import-data");
  }, [navigate]);
  const handleCharts = useCallback(() => {
    navigate("/charts");
  }, [navigate]);
  const handleAssign = async () => {
    if (!selectedDriverId || !selectedScheduleId) {
      toast.error(
        "Please select both a driver and ensure a schedule is selected."
      );
      return;
    }

    // Update the schedule's driver_auth_id field instead of creating a Driver_Ride entry
    // This ensures the driver assignment is directly linked to the specific trip/schedule
    const { error } = await supabase
      .from("Schedule")
      .update({
        driver_auth_id: selectedDriverId, // Update the driver_auth_id field
        VehicleID: parseInt(selectedVehicleId), // Also update the vehicle assignment
      })
      .eq("ScheduleID", selectedScheduleId);

    if (error) {
      console.error("Update error:", error);
      toast.error("Failed to assign driver to schedule.");
    } else {
      toast.success("Driver assigned to schedule successfully!");
      // Close modal and reset states
      setIsAssignRideModalOpen(false);
      setSelectedDriverId(null);
      setSelectedVehicleId(null);
      setSelectedScheduleId(null);
      setSelectedSchedule(null);

      // Refresh the schedules list to show the updated assignment
      const _data = getValues();
      searchSchedules({
        ..._data,
        page: page,
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      {/* Highlight glow for freshly-added Add-Trip rows. Pulses briefly on
          first paint, then holds an amber border+background until the
          manager runs another search. */}
      <style>{`
        @keyframes newlyAddedPulse {
          0%   { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.55); }
          60%  { box-shadow: 0 0 0 10px rgba(245, 158, 11, 0);   }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);   }
        }
        tr.newly-added-trip > td {
          background: linear-gradient(90deg, rgba(254, 243, 199, 0.85), rgba(254, 243, 199, 0.35));
          border-top: 1px solid #f59e0b;
          border-bottom: 1px solid #f59e0b;
        }
        tr.newly-added-trip {
          animation: newlyAddedPulse 1.6s ease-out 3;
        }
        tr.newly-added-trip > td:first-child {
          border-left: 3px solid #f59e0b;
        }
      `}</style>
      <div className="p-6 w-full pr-6 pl-6">
        {/* Header */}
        <HeaderContainer headerStyle="100px" />

        {/* Flight Details */}
        <div className="flex justify-between grid grid-cols-3 gap-4 text-center border-b pb-4 flight-details-container">
          <div className="col-span-3 col-span-3-layout mb-4">
            <p className="text-xl font-semibold">Quick Overview</p>
            <div className="flex gap-4">
              <div
                onClick={onDriverDetail}
                className="justify-between p-4 rounded-lg flex items-center more-detail-button flex-1"
              >
                <div>
                  <span>Your Drivers Details</span>
                </div>
                <div>
                  <FaArrowRight />
                </div>
              </div>
              <div
                onClick={onManagerVehicles}
                className="justify-between p-4 rounded-lg flex items-center more-detail-button flex-1"
              >
                <div>
                  <span>Your Vehicles</span>
                </div>
                <div>
                  <FaArrowRight />
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 flex justify-between gap-4 flight-details-list">
            <div className="w-50 departure-custom-card rounded-lg">
              <p className="text-gray-500">Active drivers</p>
              <p className="text-lg font-semibold">25</p>
            </div>
            <div className="departure-custom-card rounded-lg">
              <p className="text-gray-500">Assigned rides</p>
              <p className="text-lg font-semibold">19</p>
            </div>
            <div className="w-50 departure-custom-card rounded-lg">
              <p className="text-gray-500">Available vehicles</p>
              <p className="text-lg font-semibold">6</p>
            </div>
          </div>
        </div>

        {/* Live Driver Update */}
        <div className="driver-content m-4">
          <div className="driver-container mr-4">
            <p className="text-location">Driver Roster & Assignment Overview</p>

            <p>
              Easily oversee and manage your fleet with real-time driver
              statuses, assigned vehicles, and upcoming trips. Track current
              locations, update driver profiles, assign vehicles, and ensure
              compliance with safety and documentation requirements—all in one
              streamlined dashboard.
            </p>
          </div>
          <div className="departure-custom-card rounded-lg">
            <Controller
              control={control}
              name="flightNo"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between mb-2">
                  <div>
                    <p>Flight No</p>
                  </div>
                  <div>
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg"
                    />
                  </div>
                </div>
              )}
            />

            <Controller
              control={control}
              name="scheduleDate"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between mb-2">
                  <div>
                    <p>Schedule Date</p>
                  </div>
                  <div>
                    <DateInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg"
                    />
                  </div>
                </div>
              )}
            />

            <Controller
              control={control}
              name="companyID"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between mb-2">
                  <div>Company</div>
                  <CustomSelect
                    id="company-select"
                    size="small"
                    options={companyList}
                    value={value}
                    onChange={onChange}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="airlineID"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between mb-2">
                  <div>Client</div>
                  <CustomSelect
                    id="client-select"
                    size="small"
                    options={clientList}
                    value={value}
                    onChange={onChange}
                  />
                </div>
              )}
            />

          </div>
          <div className="departure-custom flex flex-col gap-2">
            <div className="w-full departure-custom-card rounded-lg">
              <Controller
                control={control}
                name="locationID"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>Airport</div>
                    <CustomSelect
                      id="location-select"
                      size="small"
                      options={locationList}
                      value={value}
                      onChange={onChange}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="passengers"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>
                      <p>Passengers</p>
                    </div>
                    <div>
                      <TextInput
                        type="number"
                        value={value}
                        onChange={onChange}
                        className="custom-input rounded-lg"
                      />
                    </div>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="driverID"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>Driver</div>
                    <CustomSelect
                      id="driver-select"
                      size="small"
                      options={driverList}
                      value={value}
                      onChange={onChange}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="vehicleID"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>Vehicle</div>
                    <CustomSelect
                      id="vehicle-select"
                      size="small"
                      options={vehicleList}
                      value={value}
                      onChange={onChange}
                    />
                  </div>
                )}
              />

            </div>

            <div className="flex justify-between mb-2">
              <button
                onClick={handleSubmit(handleSearch)}
                disabled={isSearching}
                className="justify-between p-2 pr-20 pl-20 rounded-lg flex items-center custom-button"
              >
                <span>Search</span>
              </button>
              <div>
                <div
                  onClick={onClear}
                  className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
                >
                  <span>Clear</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* {!isAdmin && (
          <div className="d-flex m-4 justify-evenly">
            <div className="lg:col-span-1 order-last lg:order-none">
              <div
                className="clean-container p-2 lg:p-0 overflow-hidden"
                style={{ minHeight: "250px" }}
              >
                <div
                  className="driver-map-mobile driver-map-tablet w-full h-full flex justify-center items-center"
                  style={{
                    height: "300px",
                    minHeight: "300px",
                  }}
                >
                  <div className="w-full h-full">
                    <LeafletMapTracking driverId="driver-001" />
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="clean-container">
                <div className="space-y-3 lg:space-y-4">
                  <div>
                    <p className="text-gray-500 text-xs lg:text-sm mb-1">
                      Destination
                    </p>
                    <p className="font-semibold text-gray-800 text-base lg:text-lg">
                      Hilton Los Angeles
                    </p>
                  </div>

                  <div>
                    <p className="text-gray-500 text-xs lg:text-sm mb-1">
                      Pickup Location
                    </p>
                    <p className="font-semibold text-gray-800 text-sm lg:text-base">
                      Terminal 4,
                    </p>
                    <p className="font-semibold text-gray-800 text-sm lg:text-base">
                      Passenger Pickup Zone
                    </p>
                  </div>

                  <div>
                    <p className="text-gray-500 text-xs lg:text-sm mb-1">
                      Estimated Drive Time
                    </p>
                    <p className="font-semibold text-gray-800 text-sm lg:text-base">
                      10 minutes
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )} */}
        <div className="manger-button-groups-container p-2 rounded-lg mb-4">
          <div className="flex align-center cursor-pointer">
            <FaWrench />
            <p className="mb-0 pl-10">Repair</p>
          </div>
          <div
            onClick={handleDriverInteraction}
            className="flex align-center cursor-pointer"
          >
            <FaThumbsUp />
            <p className="mb-0 pl-10">Driver Interaction</p>
          </div>

          <div
            onClick={handleAssignRide}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaCarSide />
            <p className="mb-0 pl-10">Assign Ride</p>
          </div>

          <div className="flex align-center cursor-pointer pl-10">
            <FaTrash />
            <p className="mb-0 pl-10">Delete</p>
          </div>

          {/* <div
            onClick={handleImport}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaFileImport />
            <p className="mb-0 pl-10">Import</p>
          </div> */}

          <div
            onClick={handleCharts}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaChartBar />
            <p className="mb-0 pl-10">Charts</p>
          </div>

          <div className="flex align-center cursor-pointer pl-10">
            <FaUserShield />
            <p className="mb-0 pl-10">Admin</p>
          </div>

          <div className="flex align-center cursor-pointer pl-10">
            <FaUndo />
            <p className="mb-0 pl-10">Undo</p>
          </div>

          <div
            onClick={handleReport}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaFileAlt />
            <p className="mb-0 pl-10">Report</p>
          </div>

          <div className="flex align-center cursor-pointer pl-10">
            <FaRedo />
            <p className="mb-0 pl-10">Refresh</p>
          </div>
        </div>

        <div className="mt-4 mr-0 ml-0" id="schedule-table">
          <div className="table-responsive">
            <MDBTable striped bordered hover>
              <MDBTableHead
                className="text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #1a56a8, #0A7AFF)",
                  color: "#fff",
                }}
              >
                <tr>
                  <th className="p-10">
                    <MDBCheckbox
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows(schedules.map((row) => row.ScheduleID));
                        } else {
                          setSelectedRows([]);
                        }
                      }}
                      checked={selectedRows.length === schedules.length}
                    />
                  </th>
                  <th className="p-10 text-center">ID</th>
                  <th className="p-10 text-center">Schedule Date (ET)</th>
                  <th className="p-10 text-center">Airline</th>
                  <th className="p-10 text-center">Flight No</th>
                  <th className="p-10 text-center">Status</th>
                  <th className="p-10 text-center">Transport</th>
                  <th className="p-10 text-center">Driver</th>
                  <th className="p-10 text-center">Vehicle</th>
                  <th className="p-10 text-center">Pilots</th>
                  <th className="p-10 text-center">F/A</th>
                  <th className="p-10 text-center">Pass</th>
                  <th className="p-10 text-center">P/U</th>
                  <th className="p-10 text-center">D/O</th>
                  <th className="p-10 text-center">Edit</th>
                  <th className="p-10 text-center">Delay</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {[...schedules]
                  // Float Add-Trip rows to the top with a temporary glow.
                  .sort((a, b) => {
                    const aNew = recentlyAddedIds.has(Number(a.ScheduleID)) ? 1 : 0;
                    const bNew = recentlyAddedIds.has(Number(b.ScheduleID)) ? 1 : 0;
                    return bNew - aNew;
                  })
                  .map((row) => {
                  const isDeleted = (row.Status || "").toUpperCase() === "DELETED";
                  const isNewlyAdded = recentlyAddedIds.has(Number(row.ScheduleID));
                  const rowStyle = isDeleted
                    ? { opacity: 0.55, textDecoration: "line-through", color: "#6b7280" }
                    : undefined;
                  const rowClass = isNewlyAdded ? "p-10 newly-added-trip" : "p-10";
                  return (
                  <tr key={row.ScheduleID} className={rowClass} style={rowStyle}>
                    <td className="p-10">
                      <MDBCheckbox
                        onChange={() => toggleCheckbox(row.ScheduleID)}
                        checked={selectedRows.includes(row.ScheduleID)}
                      />
                    </td>
                    <td className="p-10">{row.ScheduleID}</td>
                    <td className="p-10">{row.ScheduleDate}</td>
                    <td className="p-10">{row.Airline}</td>
                    <td className="p-10">
                      <a
                        href={`https://www.flightaware.com/live/flight/${row.FlightNo.split("-")[0]}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.FlightNo}
                      </a>
                    </td>
                    <td className="p-10">
                      {isDeleted ? (
                        <span style={{ color: "#b91c1c", fontWeight: 600 }}>DELETED</span>
                      ) : (
                        row.Status
                      )}
                    </td>
                    <td className="p-10">{row.Transport}</td>
                    <td className="p-10">{row.Driver}</td>
                    <td className="p-10">{row.Vehicle}</td>
                    <td className="p-10">{row.Pilots}</td>
                    <td className="p-10">{row.FA}</td>
                    <td className="p-10">{row.Pass}</td>
                    <td className="p-10">{row.PU}</td>
                    <td className="p-10">{row.DO}</td>
                    <td
                      onClick={toggleModal}
                      className="cursor-pointer flex justify-center"
                    >
                      <FaEdit />
                    </td>
                    <td className="p-10">On-Time</td>

                    {/* <td>{row.d}</td> */}
                  </tr>
                  );
                })}
              </MDBTableBody>
            </MDBTable>
          </div>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * 10 + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * 10)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * 10 > totalCount}
                    className={`pagination-item ${page * 10 > totalCount ? "disabled" : ""
                      }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Driver Info & Map Section */}
        {/* <div className="flex justify-between grid grid-cols-3 gap-6">
                    <div className="driver-info-content">
                        <div className="bg-gray-200 p-3 rounded-lg flex flex-col items-center mb-4" style={{ height: '249px' }}>
                            <img
                                src={Driver}
                                alt="Driver"
                                className="w-6 h-6 rounded-full mt-3"
                            />
                            <h2 className="font-semibold mt-4" style={{ fontSize: '28px' }}>Joseph Smith</h2>
                            <p className="text-sm text-gray-600 m-0">5+ Years Driving Experience</p>
                        </div>
                        <div onClick={toggleModal} className="bg-gray-200 justify-between  p-4 rounded-lg flex items-center mb-4 more-info-button">
                            <div>
                                <FaInfoCircle />
                            </div>
                            <div>
                                <span>More Info</span>
                            </div>
                            <div>
                                <FaArrowRight />
                            </div>
                 
                        </div>
                        <div className="bg-gray-200 flex justify-between p-4 rounded-lg flex items-center">
                            <div>
                                <FaComments />
                            </div>
                            <div>
                                <span>Chat with Joseph</span>
                            </div>
                            <div>
                                <FaArrowRight />
                            </div>
                         
                        </div>
                    </div>

                    <div className="col-span-1 col-span-1-layout flex justify-center items-center">
                        <img src={Map} alt="map" style={{ width: '100%', height: '100%' }} />
                    </div>

                    <div className="" style={{ width: '30%', border: '1px solid #383E5026', borderRadius: '0.5rem' }}>
                        <div className="p-4 card-border">
                            <div className="mb-3">
                                <p className="text-gray-500 text-sm">Destination</p>
                                <p className="font-semibold text-md">Hilton Los Angeles</p>
                            </div>
                        </div>
                        <div className="p-4 card-border">
                            <div className="mb-3">
                                <p className="text-gray-500 text-sm">Pickup Location</p>
                                <p className="font-semibold text-md">
                                    Terminal 4,
                                </p>
                                <p className="font-semibold text-md">
                                    Passenger Pickup Zone
                                </p>
                            </div>
                        </div>
                        <div className="p-4">
                            <div>
                                <p className="text-gray-500 text-sm">Estimated Drive Time</p>
                                <p className="font-semibold text-md">10 minutes</p>
                            </div>
                        </div>
                    </div>
                </div> */}
        {/* Modal */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-container h-auto w-50">
              <div className="modal-header mb-0">
                {/* <h2 className="text-xl font-semibold">Driver & Vehicle Information</h2> */}
                <button className="modal-close" onClick={toggleModal}>
                  <FaTimes size={20} />
                </button>
              </div>
              <div className="grid justify-between grid-cols-2 gap-4">
                {/* <h1>Manage Flight Assignment</h1> */}
                <p className="text-lg font-semibold">
                  Manage Flight Assignment
                </p>
                <p className="text-gray-500">
                  Update flight details, adjust schedules, add notes, or make
                  edits as needed.
                </p>
                <div
                  onClick={handleFlightStatus}
                  className="bg-gray-200 cursor-pointer flex justify-between p-4 rounded-lg items-center mt-4 mb-4"
                >
                  <div>
                    <span>Flight Status</span>
                  </div>
                  <div>
                    <FaArrowRight />
                  </div>
                </div>
                <div
                  onClick={handleScheduleChange}
                  className="bg-gray-200 cursor-pointer flex justify-between p-4 rounded-lg items-center mt-4 mb-4"
                >
                  <div>
                    <span>Schedule Change</span>
                  </div>
                  <div>
                    <FaArrowRight />
                  </div>
                </div>

                <div
                  onClick={handleAddNote}
                  className="bg-gray-200 flex justify-between p-4 rounded-lg items-center mt-4 mb-4"
                >
                  <div>
                    <span>Add Note</span>
                  </div>
                  <div>
                    <FaArrowRight />
                  </div>
                </div>
                <div
                  onClick={handleAdminLogin}
                  className="bg-gray-200 flex justify-between p-4 rounded-lg items-center mt-4 mb-4"
                >
                  <div>
                    <span>Edit(Admin Login)</span>
                  </div>
                  <div>
                    <FaArrowRight />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isAssignRideModalOpen && (
          <div className="modal-overlay">
            <div className="modal-container h-auto w-50">
              <div className="modal-header mb-0">
                {/* <h2 className="text-xl font-semibold">Driver & Vehicle Information</h2> */}
                <button
                  className="modal-close"
                  onClick={() => setIsAssignRideModalOpen(false)}
                >
                  <FaTimes size={20} />
                </button>
              </div>
              <div className="grid justify-between grid-cols-2 gap-4">
                <p className="text-lg font-semibold">
                  Assign Driver to Schedule
                </p>

                {/* Display selected schedule information */}
                {selectedSchedule && (
                  <div className="col-span-2 mb-4 p-3 bg-gray-100 rounded-lg">
                    <p className="font-semibold text-sm text-gray-700">
                      Selected Schedule:
                    </p>
                    <p className="text-sm text-gray-600">
                      Flight: {selectedSchedule.FlightNo} | Date:{" "}
                      {selectedSchedule.ScheduleDate} | Airline:{" "}
                      {selectedSchedule.Airline}
                    </p>
                  </div>
                )}
                <div className="cursor-pointer flex justify-between pt-4 ">
                  <div>
                    <span>Driver</span>
                  </div>
                  <div>
                    <select
                      className="custom-input rounded-lg"
                      value={selectedDriverId ?? ""}
                      onChange={(e) => setSelectedDriverId(e.target.value)}
                    >
                      <option value="">Select Driver</option>
                      {drivers.map((driver) => (
                        <option key={driver.DriverID} value={driver.DriverID}>
                          {driver.FirstName} {driver.LastName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="cursor-pointer flex justify-between pt-4">
                  <div>
                    <span>Car</span>
                  </div>
                  <div>
                    <select
                      className="custom-input rounded-lg"
                      value={selectedVehicleId ?? ""}
                      onChange={(e) => setSelectedVehicleId(e.target.value)}
                    >
                      <option value="">Select Vehicle</option>
                      {vehicles.map((vehicle) => (
                        <option
                          key={vehicle.VehicleID}
                          value={vehicle.VehicleID}
                        >
                          {vehicle.Code} - {vehicle.Make} {vehicle.Model}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-between mt-4 save-button">
                  <div
                    onClick={handleAssign}
                    className="justify-center p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
                  >
                    <span>Assign</span>
                  </div>
                  <div
                    onClick={() => {
                      setIsAssignRideModalOpen(false);
                      setSelectedScheduleId(null);
                      setSelectedSchedule(null);
                      setSelectedDriverId(null);
                      setSelectedVehicleId(null);
                    }}
                    className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
                  >
                    <span>Cancel</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrewContainer;
